import { provideRouter, TitleStrategy, withEnabledBlockingInitialNavigation, withRouterConfig } from '@angular/router';
import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';
import { provideStore } from '@ngrx/store';
import { GRPC_LINK_FRONTEND_SERVICE_CLIENT_SETTINGS } from '@libs/core/grpc/com/kodypay/grpc/wombat/v1/link-frontend';
import { ApplicationConfig } from '@angular/core';
import { ADYEN_CLIENT_KEY } from '@libs/feature-payments/static/tokens/adyen-client-key.token';
import { ADYEN_ENVIRONMENT } from '@libs/feature-payments/static/tokens/adyen-environment.token';
import { BRAND_ASSET_ENDPOINT } from '@libs/feature-payments/static/tokens/brand-asset-endpoint.token';
import { GOOGLE_PAY_CONFIG } from '@libs/feature-payments/static/tokens/google-pay-config.token';
import { AdyenPaymentApiService } from '@libs/feature-payments/static/interfaces/adyen-payment-api-service.interface';
import { appConfig as paymentAppConfig } from '@libs/feature-payments/static/app.config';
import packageJson from '../../package.json';
import { shopperInformationSyncReducer } from '@libs/app-kodypayments/static/+state/reducers/meta/shopper-information-sync.reducer';
import { providePaymentFeature } from '@libs/app-kodypayments/static/+state/payment.feature';
import { LinkPaymentApiService } from '@libs/app-kodypayments/static/services/link-payment-api/link-payment-api.service';
import { LAUNCH_DARKLY_CLIENT_ID } from '@libs/utility-feature-flags/tokens/launch-darkly-client-id.token';
import { APP_ENVIRONMENT, PAGE_TITLE_SEPARATOR, RecursiveTitleStrategy } from '@libs/core/utilities';
import { CORALOGIX_CONFIG } from '@libs/utility-monitoring/tokens/coralogix-config.token';
import { LOGGING_FEATURE_FLAG } from '@libs/utility-monitoring/tokens/logging-feature-flag.token';
import { featureFlags } from '@libs/utility-feature-flags/constants/feature-flags.constant';
import { SESSION_ID } from '@libs/utility-monitoring/tokens/session-id.token';
import { TRANSLATE_FILE_DIRS } from '@libs/shared/utilities/tokens/translate-file-dirs.token';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { GRPC_PAY_BY_BANK_SERVICE_CLIENT_SETTINGS } from '@libs/core/grpc/com/kodypay/grpc/paybybank/paybybank';

export const appConfig: ApplicationConfig = {
  providers: [
    paymentAppConfig.providers,
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
        metaReducers: [shopperInformationSyncReducer],
      },
    ),
    provideStoreDevtools({ connectInZone: true }),
    providePaymentFeature(),
    {
      provide: AdyenPaymentApiService,
      useExisting: LinkPaymentApiService,
    },
    { provide: APP_ENVIRONMENT, useValue: environment },
    { provide: TRANSLATE_FILE_DIRS, useValue: ['assets/i18n/payment-links', 'assets/i18n/payment-common'] },
    {
      provide: CORALOGIX_CONFIG,
      useValue: {
        ...environment.coralogix,
        environment: environment.name,
        appName: packageJson.name,
        appVersion: packageJson.version,
        labelProviders: [
          {
            providerFunc: (url: string) => {
              const linkId = url.match(/.[^.]+.([^./]+)/)?.[1];
              return linkId ? { 'kp.link_id': linkId } : {};
            },
          },
        ],
      },
    },
    {
      provide: SESSION_ID,
      useValue: `wombat-web-${window.crypto.randomUUID()}`,
    },
    { provide: LOGGING_FEATURE_FLAG, useValue: featureFlags.wombatWebCustomEventMonitoring },
    { provide: LAUNCH_DARKLY_CLIENT_ID, useValue: environment.launchDarkly.clientId },
    {
      provide: GRPC_LINK_FRONTEND_SERVICE_CLIENT_SETTINGS,
      useValue: {
        host: environment.endpoints.wombat,
        format: 'binary',
        suppressCorsPreflight: true,
      },
    },
    {
      provide: GRPC_PAY_BY_BANK_SERVICE_CLIENT_SETTINGS,
      useValue: {
        host: environment.endpoints.payByBank,
        format: 'binary',
        suppressCorsPreflight: true,
      },
    },
    {
      provide: ADYEN_CLIENT_KEY,
      useValue: environment.adyen.clientKey,
    },
    {
      provide: ADYEN_ENVIRONMENT,
      useValue: environment.adyen.environment,
    },
    {
      provide: BRAND_ASSET_ENDPOINT,
      useValue: environment.endpoints.brandAssets,
    },
    {
      provide: GOOGLE_PAY_CONFIG,
      useValue: environment.googlePay,
    },

    {
      provide: PAGE_TITLE_SEPARATOR,
      useValue: ' | ',
    },
    {
      provide: TitleStrategy,
      useExisting: RecursiveTitleStrategy,
    },
  ],
};
